import React, { useState, useEffect } from "react";
import { get, post } from "../utils/api";
import Button from "../components/Button/Button";
import ReusableTable from "../components/Table/ReusableTable";
import { FaTrash } from "react-icons/fa";
import { Plus, User, Shield, Zap, RotateCcw, Save, Trash2, Check, Loader2 } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Modify the component declaration to accept onBack prop
const ProductMapping = ({ onBack }) => {
  const [formData, setFormData] = useState({
    distributorId: null,
    distributorName: "",
    arnCode: "",
    fundCode: "",
  });

  const [distributors, setDistributors] = useState([]);
  const [fundCodes, setFundCodes] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch distributors and funds on component load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(
          "/distributorproductmapping/fetchActiveDistributorsAndFunds"
        );

        if (response.data.Success) {
          const data = response.data.Data[0];
          setDistributors(data.distributor_details || []);
          setFundCodes(data.fund_details || []);
        } else {
          console.error("Failed to load initial data.");
        }
      } catch (error) {
        console.error("Error fetching distributors/funds:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch existing fund mappings
  const fetchFundMapping = async (distributorId) => {
    if (!distributorId) {
      setTableData([]);
      return;
    }
    setLoading(true);
    try {
      const response = await post(
        "/distributorproductmapping/fetchFundMapping",
        { distributor_id: distributorId }
      );

      if (response.data.Success) {
        setTableData([...response.data.Data]); // force update
      } else {
        toast.error("Error fetching fund mapping");
        setTableData([]);
      }
    } catch (error) {
      console.error("Fetch fund mapping error:", error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input change
  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name === "distributorName") {
      const selected = distributors.find((d) => d.distributor_name === value);

      const id = selected?.distributor_sequence_number || null;
      setFormData((prev) => ({
        ...prev,
        distributorId: id,
        distributorName: value,
        arnCode: selected?.arn_code || "",
        fundCode: "",
      }));

      if (id) {
        await fetchFundMapping(id);
      } else {
        setTableData([]);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Add new mapping
  const handleAdd = async () => {
    const { distributorId, fundCode } = formData;

    if (!distributorId || !fundCode) {
      toast.info("Please select both Distributor and Fund Code.");
      return;
    }

    const fund = fundCodes.find((f) => f.fund_code === fundCode);
    if (!fund) {
      toast.error("Invalid fund selected.");
      return;
    }

    const payload = {
      distributor_id: distributorId,
      fund_id: fund.id,
      is_active: true,
      user_Id: 3, // Replace with session user ID
    };

    try {
      const response = await post(
        "/distributorproductmapping/createDistributorFundMapping",
        payload
      );

      toast.success(response.data.Message);

      if (response.data.Success) {
        await fetchFundMapping(distributorId);
        setFormData((prev) => ({ ...prev, fundCode: "" }));
      }
    } catch (error) {
      console.error("Error creating mapping:", error);
      toast.error("Failed to create mapping.");
    }
  };

  // Save all mappings
  const handleSave = async () => {
    const { distributorId } = formData;

    if (!distributorId) {
      toast.info("Please select a Distributor to save.");
      return;
    }

    const payload = {
      distributor_id: distributorId,
      user_Id: 3,
    };

    try {
      const response = await post(
        "/distributorproductmapping/saveDistributorFundMapping",
        payload
      );

      toast.success(response.data.Message);

      if (response.data.Success) {
        await fetchFundMapping(distributorId);
        if (onBack) {
          onBack(); // Call the callback function to refresh parent component
        }
      }
    } catch (error) {
      console.error("Error saving mappings:", error);
      toast.error("Failed to save mappings.");
    }
  };

  // Delete mapping
  const handleDelete = async (mapping) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this mapping?"
    );
    if (!confirmDelete) return;

    const payload = {
      id: mapping.id,
      distributor_id: mapping.distributor_id,
      fund_id: mapping.fund_id,
      user_Id: 3,
    };

    try {
      const response = await post(
        "/distributorproductmapping/deleteFundMapping",
        payload
      );

      toast.success(response.data.Message);

      if (response.data.Success) {
        await fetchFundMapping(mapping.distributor_id);
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete fund mapping.");
    }
  };

  // Table columns
  const columns = React.useMemo(
    () => [
      {
        Header: "Fund Code",
        accessor: "fund_code",
        Cell: ({ value }) => <span className="text-gray-800 font-bold text-xs uppercase">{value}</span>
      },
      {
        Header: "Class Codes",
        accessor: "class_codes",
        Cell: ({ value }) => <span className="text-gray-800 font-bold uppercase">{value}</span>
      },
      {
        Header: "Actions",
        Cell: ({ row }) => {
          const { is_new } = row.original;
          return is_new ? (
            <button
              className="text-red-500 hover:text-red-700 p-2 bg-red-50 rounded-lg transition-all border border-red-100"
              onClick={() => handleDelete(row.original)}
              title="Delete Mapping"
            >
              <Trash2 size={16} />
            </button>
          ) : null;
        },
        disableSortBy: true,
        width: 80,
      },
    ],
    []
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-100">
        <Loader2 size={32} className="animate-spin text-indigo-500 mb-4" />
        <p className="text-xs font-bold tracking-widest uppercase text-gray-400">Loading Configuration...</p>
      </div>
    );
  }

  const handleReset = () => {
    setFormData({
      distributorId: null,
      distributorName: "",
      arnCode: "",
      fundCode: "",
    });
    setTableData([]);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
        {/* Distributor Name */}
        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1 flex items-center gap-2">
            <User size={14} className="text-indigo-400" /> Distributor Name
          </label>
          <select
            name="distributorName"
            value={formData.distributorName}
            onChange={handleChange}
            className="border border-gray-200 rounded-lg px-4 py-2 w-full h-[42px] text-sm font-bold text-gray-700 bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-shadow"
          >
            <option value="">Select Distributor</option>
            {distributors.map((d) => (
              <option key={d.id} value={d.distributor_name}>
                {d.distributor_name}
              </option>
            ))}
          </select>
        </div>

        {/* ARN Code */}
        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1 flex items-center gap-2">
            <Shield size={14} className="text-indigo-400" /> APRN Code
          </label>
          <div className="h-[42px] px-4 rounded-lg bg-gray-50 border border-gray-100 flex items-center text-sm font-bold text-gray-400">
            {formData.arnCode || "-"}
          </div>
        </div>

        {/* Fund Code */}
        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1 flex items-center gap-2">
            <Zap size={14} className="text-indigo-400" /> Fund Code
          </label>
          <select
            name="fundCode"
            value={formData.fundCode}
            onChange={handleChange}
            className="border border-gray-200 rounded-lg px-4 py-2 w-full h-[42px] text-sm font-bold text-gray-700 bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-shadow"
          >
            <option value="">Select Fund Code</option>
            {fundCodes.map((f) => (
              <option key={f.id} value={f.fund_code}>
                {f.fund_code}
              </option>
            ))}
          </select>
        </div>

        {/* Add Button */}
        <button
          onClick={handleAdd}
          className="flex items-center justify-center gap-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white shadow-lg shadow-indigo-100 border-none h-[42px] px-5 rounded-lg transition-all active:scale-95 font-bold text-sm"
        >
          <Plus className="w-4 h-4" />
          Add To Grid
        </button>
      </div>

      {/* Table */}
      {tableData.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm animate-in slide-in-from-bottom-4 duration-500">
          <div className="px-5 py-3 border-b border-gray-50 bg-gray-50/50 flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-500">Mapping Preview</span>
          </div>
          <ReusableTable columns={columns} data={tableData} />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center items-center gap-4 pt-4 border-t border-gray-50">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 bg-[#10B981] hover:bg-[#059669] text-white shadow-lg shadow-emerald-100 border-none h-[42px] px-10 rounded-lg transition-all active:scale-95 font-bold text-sm"
        >
          <Check size={18} />
          Save Mapping
        </button>
        <button
          onClick={handleReset}
          className="flex items-center gap-2 bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 h-[42px] px-10 rounded-lg transition-all active:scale-95 font-bold text-sm"
        >
          <RotateCcw size={18} />
          Reset Form
        </button>
      </div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar newestOnTop theme="light" />
    </div>
  );
};

export default ProductMapping;
